
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Row, Col, Alert, Modal } from 'react-bootstrap';
import { FaUserCircle, FaEye, FaEyeSlash } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { updateUserProfile, changeUserPassword } from '../services/authService';
import { FEATURE_FLAGS } from '../constants';
import TeamNotificationTooltip from '../components/TeamNotificationTooltip';
import '../styles/enhanced-tabs.css';
import '../styles/profile-modal.css';

const Profile = ({ onClose }) => {
  const navigate = useNavigate();
  const { user, updateUserProfile: updateUserContext } = useAuth();
  const canEditCompany = user?.role === 'owner' || user?.role === 'admin';
  const [form, setForm] = useState({
    name: user?.name || user?.profile?.first_name || '',
    last_name: user?.last_name || user?.profile?.last_name || '',
    email: user?.email || '',
    accountType: user?.accountType || '',
    phone: user?.phone || '',
    company: user?.profile?.company || '',
    job_title: user?.profile?.job_title || ''
  });

  // Sync form state with user data
  React.useEffect(() => {
    setForm({
      name: user?.name || user?.profile?.first_name || '',
      last_name: user?.last_name || user?.profile?.last_name || '',
      email: user?.email || '',
      accountType: user?.accountType || '',
      phone: user?.phone || '',
      company: user?.profile?.company || '',
      job_title: user?.profile?.job_title || ''
    });
  }, [user]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);


  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Handle password change in modal
  const handlePasswordChange = async () => {
    setPasswordError(null);
    setPasswordSuccess(null);
    if (!password || !confirmPassword) {
      setPasswordError('Please fill in both password fields.');
      return;
    }
    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }
    try {
      // Get user ID from context
      const userId = user?.id || user?.userId || user?.user_id;
      if (!userId) {
        setPasswordError('User ID not found. Please log in again.');
        return;
      }

      // Call the change password service with userId
      const result = await changeUserPassword(userId, password);
      if (result.success) {
        setPasswordSuccess('Password changed successfully.');
        setPassword('');
        setConfirmPassword('');
        setTimeout(() => setShowPasswordModal(false), 1200);
      } else {
        setPasswordError(result.message || 'Failed to change password.');
      }
    } catch (err) {
      setPasswordError('Failed to change password.');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Prepare the updated profile data
      const updatedProfile = {
        name: form.name,
        last_name: form.last_name,
        phone: form.phone,
        profile: {
          ...(canEditCompany && { company: form.company }),
          job_title: form.job_title
        }
      };

      // Get user ID from context
      const userId = user?.id || user?.userId || user?.user_id;
      if (!userId) {
        setError('User ID not found. Please log in again.');
        return;
      }

      // Call the update service with userId
      const result = await updateUserProfile(userId, updatedProfile);
      
      if (result.success) {
        // Update the context with new user data
        updateUserContext(result.user);
        setSuccess('Profile updated successfully!');

        // Auto-close modal after showing success briefly
        setTimeout(() => {
          setSuccess(null);
          if (onClose) onClose();
        }, 1500);
      } else {
        setError(result.message || 'Failed to update profile');
      }
    } catch (err) {
      console.error('Profile update error:', err);
      setError('An error occurred while updating your profile');
    } finally {
      setSaving(false);
    }
  };
  return (
    <>
  <div className="modal show d-block" tabIndex="-1" style={{ display: 'block', background: 'none', position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', pointerEvents: 'auto' }}>
        <div className="modal-dialog modal-dialog-centered profile-narrow-modal" style={{ maxWidth: '900px', width: '98%' }}>
          <div className="modal-content">
            <div className="modal-header" style={{
              background: 'linear-gradient(180deg, #eef2f0 0%, #e4ece8 100%)',
              borderBottom: '1px solid #c8d8d2',
              boxShadow: '0 1px 3px rgba(15, 23, 42, 0.07)',
              borderRadius: 'calc(0.3rem - 1px) calc(0.3rem - 1px) 0 0',
              margin: 0,
              padding: '0 20px',
              minHeight: 42,
              flexShrink: 0,
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
            }}>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                {form.accountType && (
                  <div style={{
                    position: 'absolute', left: 20,
                    background: '#dcfce7', color: '#15803d',
                    padding: '3px 10px', borderRadius: 999,
                    fontSize: 12, fontWeight: 600,
                    border: '1px solid #bbf7d0',
                    letterSpacing: '0.01em',
                  }}>
                    {form.accountType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} Plan
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    width: 24, height: 24, borderRadius: 7,
                    background: '#d1fae5', color: '#047857',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16,
                  }}><FaUserCircle /></div>
                  <h2 style={{
                    margin: 0, color: '#1e3a2f',
                    fontSize: 18, fontWeight: 700,
                    letterSpacing: '-0.01em',
                  }}>Profile</h2>
                </div>
              </div>
              <button
                type="button"
                aria-label="Close"
                style={{
                  position: 'absolute',
                  top: '50%',
                  right: 12,
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  background: 'none',
                  border: 'none',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  outline: 'none',
                  padding: 0,
                  transition: 'background 0.2s, transform 0.2s',
                  fontSize: '1.4rem',
                  color: '#4b6360',
                  lineHeight: 1,
                }}
                onClick={() => onClose ? onClose() : navigate('/register')}
                onMouseOver={e => {
                  e.currentTarget.style.background = 'rgba(0,0,0,0.07)';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.background = 'none';
                }}
                tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') (onClose ? onClose() : navigate('/register')); }}
              >
                ×
              </button>
            </div>
            <div className="modal-body p-4" style={{ borderRadius: 0 }}>
              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}
              <Form onSubmit={handleSave}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email</Form.Label>
                      <Form.Control type="email" value={form.email} disabled readOnly />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        Account Type
                        {form.accountType && (
                          form.accountType.toLowerCase().includes('team') ||
                          form.accountType.toLowerCase().includes('brokerage') ||
                          form.accountType.toLowerCase().includes('assistant')
                        ) && (
                          <TeamNotificationTooltip placement="right">
                            <span style={{ 
                              color: '#3b82f6', 
                              cursor: 'help',
                              fontSize: '14px'
                            }}>
                              ℹ️
                            </span>
                          </TeamNotificationTooltip>
                        )}
                      </Form.Label>
                      <Form.Control type="text" value={form.accountType} disabled readOnly />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>First Name</Form.Label>
                      <Form.Control name="name" value={form.name} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Last Name</Form.Label>
                      <Form.Control name="last_name" value={form.last_name} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  {FEATURE_FLAGS.PHONE_FEATURE_ENABLED && (
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Phone</Form.Label>
                        <Form.Control name="phone" value={form.phone} onChange={handleChange} />
                      </Form.Group>
                    </Col>
                  )}
                  <Col md={FEATURE_FLAGS.PHONE_FEATURE_ENABLED ? 6 : 12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Company</Form.Label>
                      <Form.Control
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        disabled={!canEditCompany}
                        readOnly={!canEditCompany}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <div className="d-flex justify-content-between">
                  <Button
                    style={{
                      background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                      border: 'none',
                      color: '#333',
                      boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                      transition: 'all 0.2s ease-in-out',
                      borderRadius: '4px',
                      fontWeight: '500',
                      padding: '4px 12px'
                    }}
                    onClick={() => onClose ? onClose() : navigate('/register')}
                  >
                    Close
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="ms-2"
                    style={{
                      background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                      border: 'none',
                      color: '#333',
                      boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                      transition: 'all 0.2s ease-in-out',
                      borderRadius: '4px',
                      fontWeight: '500',
                      padding: '4px 12px'
                    }}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button
                    style={{
                      background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                      border: 'none',
                      color: '#333',
                      boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                      transition: 'all 0.2s ease-in-out',
                      borderRadius: '4px',
                      fontWeight: '500',
                      padding: '4px 12px'
                    }}
                    onClick={() => setShowPasswordModal(true)}
                  >
                    Change Password
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
  <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} dialogClassName="profile-narrow-modal" style={{ zIndex: 2000, position: 'fixed' }} backdropClassName="modal-backdrop show" >
        <Modal.Header style={{
          color: 'white',
          borderBottom: '1px solid #e9ecef',
          background: 'none',
          position: 'relative',
          minHeight: 64
        }}>
          <h2 className="mb-4" style={{ fontWeight: 600, letterSpacing: '0.5px', color: '#222', textShadow: '0 1px 2px #fff', display: 'flex', alignItems: 'center', fontSize: '2rem', margin: 0 }}>
            Change Password
          </h2>
          <button
            type="button"
            className="btn-close enhanced-close-btn"
            aria-label="Close"
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 10,
              background: 'none',
              border: 'none',
              borderRadius: '50%',
              width: '44px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 'none',
              transition: 'background 0.2s, transform 0.2s',
              fontSize: '2.2rem',
              cursor: 'pointer',
              outline: 'none',
              padding: 0
            }}
            onClick={() => setShowPasswordModal(false)}
            onMouseOver={e => {
              e.currentTarget.style.background = 'rgba(0,0,0,0.04)';
              e.currentTarget.style.transform = 'scale(1.13)';
            }}
            onMouseOut={e => {
              e.currentTarget.style.background = 'none';
              e.currentTarget.style.transform = 'scale(1)';
            }}
            tabIndex={0}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setShowPasswordModal(false); }}
          >
            <span style={{ fontSize: '2.2rem', color: '#181818', fontWeight: 900, lineHeight: 1 }}>×</span>
          </button>
        </Modal.Header>
        <Modal.Body>
          {passwordError && <Alert variant="danger">{passwordError}</Alert>}
          {passwordSuccess && <Alert variant="success">{passwordSuccess}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label>New Password</Form.Label>
            <div style={{ position: 'relative' }}>
              <Form.Control
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoFocus
                style={{ paddingRight: '40px' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '5px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#666'
                }}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
              </button>
            </div>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Confirm New Password</Form.Label>
            <div style={{ position: 'relative' }}>
              <Form.Control
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                style={{ paddingRight: '40px' }}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '5px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#666'
                }}
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              >
                {showConfirmPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
              </button>
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handlePasswordChange}
            disabled={!password || !confirmPassword || password !== confirmPassword}
          >
            Change Password
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default Profile;
